import React from 'react';
import {
  RecommendationFilters as Filters,
  RecommendationCategory,
  CloudProvider,
  RiskTier,
} from '../../types/recommendation';

/**
 * Recommendation Filters Component
 * AC 1: Filters for category, cloud, risk tier, and status
 */

interface RecommendationFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}

export const RecommendationFilters: React.FC<RecommendationFiltersProps> = ({
  filters,
  onFiltersChange,
}) => {
  const handleCategoryToggle = (category: RecommendationCategory) => {
    const categories = filters.categories || [];
    const newCategories = categories.includes(category)
      ? categories.filter((c) => c !== category)
      : [...categories, category];
    onFiltersChange({ ...filters, categories: newCategories });
  };

  const handleCloudProviderToggle = (provider: CloudProvider) => {
    const providers = filters.cloudProviders || [];
    const newProviders = providers.includes(provider)
      ? providers.filter((p) => p !== provider)
      : [...providers, provider];
    onFiltersChange({ ...filters, cloudProviders: newProviders });
  };

  const handleRiskTierToggle = (tier: RiskTier) => {
    const tiers = filters.riskTiers || [];
    const newTiers = tiers.includes(tier)
      ? tiers.filter((t) => t !== tier)
      : [...tiers, tier];
    onFiltersChange({ ...filters, riskTiers: newTiers });
  };

  const handleSearchChange = (query: string) => {
    onFiltersChange({ ...filters, searchQuery: query });
  };

  const handleMinSavingsChange = (value: string) => {
    const minSavings = value ? parseFloat(value) : undefined;
    onFiltersChange({ ...filters, minSavings });
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters =
    (filters.categories && filters.categories.length > 0) ||
    (filters.cloudProviders && filters.cloudProviders.length > 0) ||
    (filters.riskTiers && filters.riskTiers.length > 0) ||
    filters.minSavings !== undefined ||
    filters.searchQuery;

  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-4 dark:border-border dark:bg-card">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-primary hover:text-primary dark:text-primary dark:hover:text-primary"
            aria-label="Clear all filters"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Search */}
      <div>
        <label htmlFor="search" className="block text-sm font-medium text-foreground/80">
          Search
        </label>
        <input
          type="text"
          id="search"
          value={filters.searchQuery || ''}
          onChange={(e) => handleSearchChange(e.target.value)}
          placeholder="Search recommendations..."
          className="mt-1 block w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
          aria-label="Search recommendations"
        />
      </div>

      {/* Category Filter */}
      <div>
        <label className="block text-sm font-medium text-foreground/80">
          Category
        </label>
        <div className="mt-2 space-y-2">
          {Object.values(RecommendationCategory).map((category) => (
            <label key={category} className="flex items-center">
              <input
                type="checkbox"
                checked={filters.categories?.includes(category) || false}
                onChange={() => handleCategoryToggle(category)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring dark:border-border dark:bg-secondary"
                aria-label={`Filter by ${category}`}
              />
              <span className="ml-2 text-sm text-foreground/80">
                {category.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Cloud Provider Filter */}
      <div>
        <label className="block text-sm font-medium text-foreground/80">
          Cloud Provider
        </label>
        <div className="mt-2 space-y-2">
          {Object.values(CloudProvider).map((provider) => (
            <label key={provider} className="flex items-center">
              <input
                type="checkbox"
                checked={filters.cloudProviders?.includes(provider) || false}
                onChange={() => handleCloudProviderToggle(provider)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring dark:border-border dark:bg-secondary"
                aria-label={`Filter by ${provider}`}
              />
              <span className="ml-2 text-sm text-foreground/80">
                {provider.toUpperCase()}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Risk Tier Filter */}
      <div>
        <label className="block text-sm font-medium text-foreground/80">
          Risk Tier
        </label>
        <div className="mt-2 space-y-2">
          {Object.values(RiskTier).map((tier) => (
            <label key={tier} className="flex items-center">
              <input
                type="checkbox"
                checked={filters.riskTiers?.includes(tier) || false}
                onChange={() => handleRiskTierToggle(tier)}
                className="h-4 w-4 rounded border-border text-primary focus:ring-ring dark:border-border dark:bg-secondary"
                aria-label={`Filter by ${tier} risk`}
              />
              <span className="ml-2 text-sm text-foreground/80">
                {tier.charAt(0).toUpperCase() + tier.slice(1)} Risk
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* State filtering lives in the group chips + state dropdown above
          the table (Phase 4.0) - one place, built from the server's state
          catalogue, instead of a second hard-coded list here. */}

      {/* Minimum Savings Filter */}
      <div>
        <label htmlFor="min-savings" className="block text-sm font-medium text-foreground/80">
          Minimum Monthly Savings ($)
        </label>
        <input
          type="number"
          id="min-savings"
          value={filters.minSavings || ''}
          onChange={(e) => handleMinSavingsChange(e.target.value)}
          placeholder="0"
          min="0"
          step="100"
          className="mt-1 block w-full rounded-md border border-border px-3 py-2 text-sm shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring dark:border-border dark:bg-secondary dark:text-white"
          aria-label="Minimum monthly savings filter"
        />
      </div>
    </div>
  );
};
